import { Menu } from '@linktivity/link-ui';
import {
  UserGroup,
  UserGroupEnum,
  UserRole,
  UserRoleEnum
} from '@nankai/admin/constants';
import { useAuth } from '@nankai/admin/context/AuthContext';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import styles from './sidebar.module.css';

const Sidebar = observer(() => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userRole = user!.userRole as UserRole;
  const userGroup = user!.userGroup as UserGroup;
  const isProduction = import.meta.env.VITE_APP_ADMIN_ENV === 'production';

  // 指令/本社：すべて表示
  // 駅：時刻表、空席状況照会
  // 車内：列車予約一覧、空席状況照会、時刻表
  // 協力会社：列車予約一覧、空席状況照会、時刻表
  // 駅・車内・協力会社は管理者も担当者も「アカウント管理」は非表示

  const isCommandCenterOrHeadOffice =
    userGroup === UserGroupEnum.CommandCenter ||
    userGroup === UserGroupEnum.HeadOffice;
  const isStation = userGroup === UserGroupEnum.Station;
  const isContractorOrOnboard =
    userGroup === UserGroupEnum.Contractor ||
    userGroup === UserGroupEnum.Onboard;
  const canSeeMasterManager = isCommandCenterOrHeadOffice;
  const canSeeTrainOrders =
    isCommandCenterOrHeadOffice || isContractorOrOnboard;
  const canSeeSeatAvailability =
    isCommandCenterOrHeadOffice || isStation || isContractorOrOnboard;
  const canSeeStationSeatingTimetable =
    isCommandCenterOrHeadOffice || isStation || isContractorOrOnboard;
  const canSeeServiceManager =
    canSeeTrainOrders ||
    canSeeSeatAvailability ||
    canSeeStationSeatingTimetable;
  const canSeeUserManagement =
    isCommandCenterOrHeadOffice && userRole === UserRoleEnum.Admin;

  return (
    <aside className={styles.sidebar}>
      <div className={styles.inner}>
        <Menu className={styles.menu} accordion>
          <Menu.MenuItem label={t('views.dashboard.title')} to="/dashboard" />
          <>
            {canSeeMasterManager && (
              <Menu.SubMenu
                label={t('layouts.sidebar.masterManager')}
                name="masterManager"
              >
                <Menu.MenuItem
                  label={t('layouts.sidebar.seatAdjustmentSetting')}
                  to="/trains"
                />
                <Menu.MenuItem
                  label={t('layouts.sidebar.stationStopPatternSetting')}
                  to="/station-stop-patterns"
                />
                <Menu.MenuItem
                  label={t('layouts.sidebar.trainSchedule')}
                  to="/train-schedules"
                />
                <Menu.MenuItem
                  label={t('layouts.sidebar.calendar')}
                  to="/calendars"
                />
                <Menu.MenuItem
                  label={t('layouts.sidebar.trainAssignment')}
                  to="/train-assignments"
                />
                <Menu.MenuItem
                  label={t('layouts.sidebar.applies')}
                  to="/applies"
                />
                <Menu.MenuItem
                  label={t('layouts.sidebar.suspension')}
                  to="/suspensions"
                />
              </Menu.SubMenu>
            )}

            {canSeeServiceManager && (
              <Menu.SubMenu
                label={t('layouts.sidebar.serviceManager')}
                name="serviceManager"
              >
                {isCommandCenterOrHeadOffice && (
                  <Menu.MenuItem
                    label={t('layouts.sidebar.inventoryTrainSeat')}
                    to="/inventory/trains"
                  />
                )}
                {canSeeTrainOrders && (
                  <Menu.MenuItem
                    label={t('layouts.sidebar.trainOrders')}
                    to="/orders"
                  />
                )}
                {isCommandCenterOrHeadOffice && (
                  <Menu.MenuItem
                    label={t('layouts.sidebar.specialSeatAdjustmentUpdate')}
                    to="/special-seat-adjustments"
                  />
                )}
                {isCommandCenterOrHeadOffice && (
                  <Menu.MenuItem
                    label={t('layouts.sidebar.seatOrderHistory')}
                    to="/seat-order-history"
                  />
                )}
                {canSeeSeatAvailability && (
                  <Menu.MenuItem
                    label={t('layouts.sidebar.seatAvailabilityInquiry')}
                    to="/seat-availability"
                  />
                )}
                {canSeeStationSeatingTimetable && (
                  <Menu.MenuItem
                    label={t('layouts.sidebar.stationSeatingTimetable')}
                    to="/station-seating-timetable"
                  />
                )}
              </Menu.SubMenu>
            )}

            <Menu.SubMenu
              label={t('layouts.sidebar.userManager')}
              name="userManager"
            >
              {canSeeUserManagement && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.userManagement')}
                  to="/users"
                />
              )}
              <Menu.MenuItem
                label={t('layouts.sidebar.passwordUpdate')}
                to="/password/update"
              />
              {isCommandCenterOrHeadOffice && (
                <Menu.MenuItem
                  label={t('layouts.sidebar.operationLog')}
                  to="/operations"
                />
              )}
            </Menu.SubMenu>
          </>
        </Menu>
      </div>
    </aside>
  );
});

export default Sidebar;
